import rad from './rad.json';
import lickly from './lickly.json';
import radAmplify from './rad-amplify.json';

// Brand detection — AAPU-5656.
//
// Historically the brand was baked into the bundle at build time via
// `REACT_APP_BRAND`, which forced 3 separate builds + 3 deploy workflows
// to ship a single feature change. We now detect the brand at runtime
// from `window.location.hostname` so a single bundle can serve all 3
// domains from a single Nginx server block.
//
// `REACT_APP_BRAND` still works as an explicit override (useful for
// `npm start` dev loop, Storybook, Jest, and any deployment that doesn't
// match the canonical hostname patterns below).
const HOSTNAME_TO_BRAND = [
  { match: (h) => /(^|\.)radintel\.(ai|com)$/i.test(h), brand: 'radintel' },
  { match: (h) => /(^|\.)radamplify\.com$/i.test(h), brand: 'radamplify' },
  { match: (h) => /(^|\.)lickly\.(com|loc)$/i.test(h), brand: 'lickly' },
];

function detectBrandFromHostname() {
  if (typeof window === 'undefined' || !window.location) {
    return null; // SSR / Jest / Node — fall through to env or default
  }
  const host = (window.location.hostname || '').toLowerCase();
  if (!host) return null;
  for (let i = 0; i < HOSTNAME_TO_BRAND.length; i += 1) {
    if (HOSTNAME_TO_BRAND[i].match(host)) return HOSTNAME_TO_BRAND[i].brand;
  }
  return null;
}

// Resolution order:
//   1. REACT_APP_BRAND env override (explicit > implicit)
//   2. window.location.hostname (browser runs — the AAPU-5656 default)
//   3. 'lickly' fallback (matches historical default)
const BRAND = process.env.REACT_APP_BRAND || detectBrandFromHostname() || 'lickly';

// eslint-disable-next-line import/no-mutable-exports
let configData;

switch (BRAND.toLowerCase()) {
  case 'lickly':
    configData = { ...lickly };
    break;
  case 'rad':
  case 'radintel':
    configData = { ...rad };
    break;
  case 'radamplify':
    configData = { ...radAmplify };
    break;

  default:
    configData = { ...lickly };
}

// Per-brand Froala editor license keys
const BRAND_FROALA_KEYS = {
  rad: process.env.REACT_APP_FROALA_LICENSE_KEY_RAD,
  radintel: process.env.REACT_APP_FROALA_LICENSE_KEY_RAD,
  lickly: process.env.REACT_APP_FROALA_LICENSE_KEY_LICKLY,
  radamplify: process.env.REACT_APP_FROALA_LICENSE_KEY_RAD_AMPLIFY,
};
if (BRAND_FROALA_KEYS[BRAND.toLowerCase()]) {
  configData.froalaEditorLicenseKey = BRAND_FROALA_KEYS[BRAND.toLowerCase()];
}

// HubSpot form/portal IDs — moved out of the tracked config files and into CI
// secrets. Shared portal ID, per-brand short/complete signup form IDs.
const BRAND_HUBSPOT_SHORT_FORM_IDS = {
  rad: process.env.REACT_APP_HUBSPOT_RADINTEL_SHORT_FORM_ID,
  radintel: process.env.REACT_APP_HUBSPOT_RADINTEL_SHORT_FORM_ID,
  lickly: process.env.REACT_APP_HUBSPOT_LICKLY_SHORT_FORM_ID,
  radamplify: process.env.REACT_APP_HUBSPOT_RADAMPLIFY_SHORT_FORM_ID,
};
const BRAND_HUBSPOT_COMPLETE_FORM_IDS = {
  rad: process.env.REACT_APP_HUBSPOT_RADINTEL_COMPLETE_FORM_ID,
  radintel: process.env.REACT_APP_HUBSPOT_RADINTEL_COMPLETE_FORM_ID,
  lickly: process.env.REACT_APP_HUBSPOT_LICKLY_COMPLETE_FORM_ID,
  radamplify: process.env.REACT_APP_HUBSPOT_RADAMPLIFY_COMPLETE_FORM_ID,
};

configData.hubspotPortalId = process.env.REACT_APP_HUBSPOT_PORTAL_ID || null;
configData.hubspotShortSignupFormId = BRAND_HUBSPOT_SHORT_FORM_IDS[BRAND.toLowerCase()] || null;
configData.hubspotCompleteSignupFormId =
  BRAND_HUBSPOT_COMPLETE_FORM_IDS[BRAND.toLowerCase()] || null;

// Override with environment variables if provided
// (Userflow tokens were previously gated on REACT_APP_BRAND === 'lickly';
// switching to runtime detection means the gate is now BRAND === 'lickly'
// — keeps behaviour identical for both build-env and hostname-driven runs.)
if (
  process.env.REACT_APP_USERFLOW_TOKEN_STAGING &&
  process.env.REACT_APP_API_ENV === 'staging' &&
  BRAND.toLowerCase() === 'lickly'
) {
  configData.userFlowToken = process.env.REACT_APP_USERFLOW_TOKEN_STAGING;
}

if (
  process.env.REACT_APP_USERFLOW_TOKEN_PRODUCTION &&
  process.env.REACT_APP_API_ENV === 'production' &&
  BRAND.toLowerCase() === 'lickly'
) {
  configData.userFlowToken = process.env.REACT_APP_USERFLOW_TOKEN_PRODUCTION;
}

if (process.env.REACT_APP_ENABLE_TRACKJS !== undefined) {
  configData.enableTrackJS = process.env.REACT_APP_ENABLE_TRACKJS === 'true';
}

export default configData;
